// frontend/src/pages/Attendance.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import {
    CheckCircleIcon as CheckCircle,
    CloseIcon as X,
    CalendarIcon as Calendar,
    ClockIcon as Clock,
    AddIcon as Add
} from '../components/Icons';

const AttendanceShimmer = () => (
    <div className="container mx-auto p-4 max-w-7xl animate-pulse">
        {/* Header Skeleton */}
        <div className="mb-8 flex items-center gap-3">
            <div className="w-12 h-12 bg-surface-containerHigh rounded-xl" />
            <div className="space-y-2">
                <div className="h-8 bg-surface-containerHigh rounded-xl w-48" />
                <div className="h-4 bg-surface-containerHigh rounded-lg w-64" />
            </div>
        </div>

        {/* Group Selector Skeleton */}
        <div className="bg-surface-containerLow border border-outlineVariant/30 rounded-[2rem] p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1 space-y-2">
                    <div className="h-4 bg-surface-containerHigh rounded-lg w-32" />
                    <div className="h-12 bg-surface-containerHigh rounded-2xl w-full" />
                </div>
                <div className="h-12 bg-surface-containerHigh rounded-full w-48" />
            </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Sessions Skeleton */}
            <div className="bg-surface-containerLow border border-outlineVariant/30 rounded-[2rem] p-6 space-y-4">
                <div className="h-6 bg-surface-containerHigh rounded-lg w-32 mb-2" />
                {[1, 2, 3].map(i => (
                    <div key={i} className="p-4 rounded-xl border border-outlineVariant/30 space-y-2">
                        <div className="flex justify-between">
                            <div className="h-5 bg-surface-containerHigh rounded-lg w-24" />
                            <div className="h-6 bg-surface-containerHigh rounded-full w-10" />
                        </div>
                        <div className="h-4 bg-surface-containerHigh rounded-lg w-16" />
                        <div className="flex gap-2">
                            <div className="h-5 bg-surface-containerHigh rounded-full w-8" />
                            <div className="h-5 bg-surface-containerHigh rounded-full w-8" />
                        </div>
                    </div>
                ))}
            </div>

            {/* Current Session Skeleton */}
            <div className="lg:col-span-2 bg-surface-containerLow border border-outlineVariant/30 rounded-[2rem] p-6 space-y-6">
                <div className="flex justify-between items-start">
                    <div className="space-y-2">
                        <div className="h-6 bg-surface-containerHigh rounded-lg w-40" />
                        <div className="h-4 bg-surface-containerHigh rounded-lg w-32" />
                    </div>
                    <div className="space-y-2 items-end flex flex-col">
                        <div className="h-8 bg-surface-containerHigh rounded-xl w-16" />
                        <div className="h-3 bg-surface-containerHigh rounded-lg w-24" />
                    </div>
                </div>

                {/* Quick Stats Skeleton */}
                <div className="grid grid-cols-4 gap-3">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="p-3 bg-surface-containerHigh/50 rounded-xl space-y-1">
                            <div className="h-8 bg-surface-containerHigh rounded-xl mx-auto w-8" />
                            <div className="h-3 bg-surface-containerHigh rounded-lg mx-auto w-12" />
                        </div>
                    ))}
                </div>

                {/* Members List Skeleton */}
                <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="flex items-center justify-between p-4 bg-surface-containerHigh/30 rounded-xl">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-surface-containerHigh rounded-full" />
                                <div className="space-y-1">
                                    <div className="h-5 bg-surface-containerHigh rounded-lg w-32" />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                {[1, 2, 3, 4].map(j => (
                                    <div key={j} className="w-10 h-10 bg-surface-containerHigh rounded-full" />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

const Attendance = () => {
    const { API_URL, user } = useContext(AuthContext);
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [currentSession, setCurrentSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showNewSessionModal, setShowNewSessionModal] = useState(false);
    const [members, setMembers] = useState([]);

    const [newSession, setNewSession] = useState({
        date: new Date().toISOString().split('T')[0],
        title: 'Gruppenstunde',
        type: 'gruppenstunde'
    });

    const statusOptions = [
        { value: 'present', label: 'Anwesend', color: 'bg-primary-container text-primary-onContainer', icon: '✓' },
        { value: 'excused', label: 'Entschuldigt', color: 'bg-secondary-container text-secondary-onContainer', icon: 'E' },
        { value: 'unexcused', label: 'Unentschuldigt', color: 'bg-error-container text-error-on-container', icon: '✗' },
        { value: 'late', label: 'Verspätet', color: 'bg-tertiary-container text-tertiary-onContainer', icon: 'V' }
    ];

    useEffect(() => {
        fetchGroups();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (selectedGroup) {
            fetchAttendance();
            fetchGroupMembers();
        }
        // eslint-disable-next-line
    }, [selectedGroup]);

    const fetchGroups = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/groups`);
            setGroups(data);
            if (data.length > 0) {
                setSelectedGroup(data[0]._id);
            }
            setLoading(false);
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Laden der Gruppen' });
            setLoading(false);
        }
    };

    const fetchAttendance = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const { data } = await axios.get(`${API_URL}/attendance/group/${selectedGroup}`, config);
            setAttendanceRecords(data);
        } catch (error) {
            console.error('Error fetching attendance:', error);
        }
    };

    const fetchGroupMembers = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/groups/${selectedGroup}`);
            setMembers(data.members || []);
        } catch (error) {
            console.error('Error fetching members:', error);
        }
    };

    const createNewSession = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };

            // Create session with all members defaulting to present (standard scout workflow)
            const records = members.map(m => ({
                user: m._id,
                status: 'present'
            }));

            const { data } = await axios.post(`${API_URL}/attendance`, {
                group: selectedGroup,
                date: newSession.date,
                title: newSession.title,
                type: newSession.type,
                records
            }, config);

            setCurrentSession(data);
            setShowNewSessionModal(false);
            fetchAttendance();
            setMessage({ type: 'success', text: 'Anwesenheitsliste erstellt (Standard: alle anwesend)!' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Erstellen' });
        }
    };

    const updateStatus = async (userId, newStatus) => {
        if (!currentSession) return;

        const updatedRecords = currentSession.records.map(r =>
            (r.user._id || r.user) === userId ? { ...r, status: newStatus } : r
        );

        // Optimistic UI update
        setCurrentSession(prev => ({ ...prev, records: updatedRecords }));

        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.post(`${API_URL}/attendance`, {
                ...currentSession,
                records: updatedRecords
            }, config);
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Aktualisieren' });
        }
    };

    const handleBatchSetAll = async (newStatus) => {
        if (!currentSession) return;

        const updatedRecords = currentSession.records.map(r => ({
            ...r,
            status: newStatus
        }));

        // Optimistic UI update
        setCurrentSession(prev => ({ ...prev, records: updatedRecords }));

        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.post(`${API_URL}/attendance`, {
                ...currentSession,
                records: updatedRecords
            }, config);
            setMessage({ type: 'success', text: `Alle als "${statusOptions.find(o => o.value === newStatus)?.label}" markiert!` });
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Ausführen der Sammelaktion' });
        }
    };

    const openSession = (session) => {
        setCurrentSession(session);
    };

    const calculateStats = (records) => {
        if (!records || records.length === 0) return { present: 0, excused: 0, unexcused: 0, late: 0, rate: 0 };

        const stats = { present: 0, excused: 0, unexcused: 0, late: 0 };
        records.forEach(r => {
            if (stats[r.status] !== undefined) stats[r.status]++;
        });
        stats.rate = Math.round(((stats.present + stats.late) / records.length) * 100);
        return stats;
    };

    if (loading) {
        return <AttendanceShimmer />;
    }

    return (
        <div className="container mx-auto p-4 max-w-7xl animate-fade-in">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-xl bg-primary-container text-primary-onContainer flex items-center justify-center shadow-sm">
                        <CheckCircle style={{ fontSize: 28 }} />
                    </div>
                    <div>
                        <h1 className="text-display-small font-bold text-surface-on">Anwesenheitsliste</h1>
                        <p className="text-body-medium text-surface-onVariant">Erfassen und verwalten Sie die Anwesenheit</p>
                    </div>
                </div>
            </div>

            {/* Alert */}
            {message.text && (
                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 animate-fade-in ${message.type === 'error' ? 'bg-error-container text-error-on-container' : 'bg-primary-container text-primary-onContainer'
                    }`}>
                    <span>{message.text}</span>
                    <button onClick={() => setMessage({ type: '', text: '' })} className="ml-auto">
                        <X style={{ fontSize: 18 }} />
                    </button>
                </div>
            )}

            {/* Group Selector */}
            <div className="md3-card-elevated p-6 mb-6">
                <div className="flex flex-col md:flex-row gap-4 items-end">
                    <div className="flex-1">
                        <label className="block text-label-large text-surface-onVariant mb-2">Gruppe auswählen</label>
                        <select
                            value={selectedGroup}
                            onChange={(e) => { setSelectedGroup(e.target.value); setCurrentSession(null); }}
                            className="md3-textfield-outlined"
                        >
                            {groups.map(g => (
                                <option key={g._id} value={g._id}>{g.name}</option>
                            ))}
                        </select>
                    </div>
                    <button
                        onClick={() => setShowNewSessionModal(true)}
                        className="md3-btn-filled flex items-center gap-2"
                        disabled={!selectedGroup}
                    >
                        <Add style={{ fontSize: 20 }} />
                        Neue Anwesenheitsliste
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Sessions */}
                <div className="md3-card-outlined p-6">
                    <h2 className="text-title-medium font-bold mb-4 flex items-center gap-2">
                        <Calendar style={{ fontSize: 20 }} />
                        Letzte Termine
                    </h2>
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                        {attendanceRecords.map(record => {
                            const stats = calculateStats(record.records);
                            return (
                                <button
                                    key={record._id}
                                    onClick={() => openSession(record)}
                                    className={`w-full text-left p-4 rounded-xl border transition-colors ${currentSession?._id === record._id
                                            ? 'border-primary bg-primary-container/30'
                                            : 'border-outlineVariant hover:bg-surface-containerLow'
                                        }`}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <p className="font-bold text-body-large">{record.title || 'Termin'}</p>
                                            <p className="text-body-small text-surface-onVariant">
                                                {new Date(record.date).toLocaleDateString('de-DE')}
                                            </p>
                                        </div>
                                        <span className="text-title-medium font-bold text-primary">{stats.rate}%</span>
                                    </div>
                                    <div className="flex gap-2 text-label-small">
                                        <span className="px-2 py-0.5 rounded-full bg-primary-container text-primary-onContainer">
                                            ✓ {stats.present}
                                        </span>
                                        <span className="px-2 py-0.5 rounded-full bg-error-container text-error-on-container">
                                            ✗ {stats.unexcused}
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                        {attendanceRecords.length === 0 && (
                            <p className="text-center text-surface-onVariant italic py-8">
                                Noch keine Anwesenheitslisten
                            </p>
                        )}
                    </div>
                </div>

                {/* Current Session */}
                <div className="lg:col-span-2 md3-card-elevated p-6">
                    {currentSession ? (
                        <>
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h2 className="text-title-large font-bold">{currentSession.title}</h2>
                                    <p className="text-body-medium text-surface-onVariant">
                                        {new Date(currentSession.date).toLocaleDateString('de-DE', {
                                            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                                        })}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-headline-small font-bold text-primary">
                                        {calculateStats(currentSession.records).rate}%
                                    </p>
                                    <p className="text-body-small text-surface-onVariant">Anwesenheitsquote</p>
                                </div>
                            </div>

                            {/* Quick Stats */}
                            <div className="grid grid-cols-4 gap-3 mb-6">
                                {statusOptions.map(opt => {
                                    const count = currentSession.records?.filter(r => r.status === opt.value).length || 0;
                                    return (
                                        <div key={opt.value} className={`p-3 rounded-xl text-center ${opt.color}`}>
                                            <p className="text-headline-small font-bold">{count}</p>
                                            <p className="text-body-small">{opt.label}</p>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Members List */}
                            <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                                {currentSession.records?.map(record => {
                                    const member = record.user;
                                    const memberName = member?.userData?.scoutName 
                                        ? `${member.userData.scoutName} (${member.userData.firstName || ''})`
                                        : `${member?.userData?.firstName || ''} ${member?.userData?.lastName || ''}`.trim() || 'Unbekannt';

                                    return (
                                        <div key={member?._id || record._id} className="flex items-center justify-between p-4 bg-surface-containerLow rounded-xl">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-secondary-container text-secondary-onContainer flex items-center justify-center font-bold">
                                                    {memberName.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-body-large">{memberName}</p>
                                                    {record.checkInTime && (
                                                        <p className="text-body-small text-surface-onVariant flex items-center gap-1">
                                                            <Clock style={{ fontSize: 12 }} />
                                                            {new Date(record.checkInTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                {statusOptions.map(opt => (
                                                    <button
                                                        key={opt.value}
                                                        onClick={() => updateStatus(member?._id || record.user, opt.value)}
                                                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${record.status === opt.value
                                                                ? `${opt.color} ring-2 ring-offset-2 ring-primary`
                                                                : 'bg-surface-variant text-surface-onVariant hover:opacity-80'
                                                            }`}
                                                        title={opt.label}
                                                    >
                                                        {opt.icon}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-16 text-surface-onVariant">
                            <CheckCircle style={{ fontSize: 64 }} className="mx-auto mb-4 opacity-30" />
                            <p className="text-title-medium">Wählen Sie einen Termin aus</p>
                            <p className="text-body-medium">oder erstellen Sie eine neue Anwesenheitsliste</p>
                        </div>
                    )}
                </div>
            </div>

            {/* New Session Modal */}
            {showNewSessionModal && (
                <div className="fixed inset-0 bg-scrim/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="md3-card-elevated max-w-md w-full p-8 animate-scale-in">
                        <h2 className="text-title-large font-bold mb-6">Neue Anwesenheitsliste</h2>

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2">Datum</label>
                                <input
                                    type="date"
                                    value={newSession.date}
                                    onChange={(e) => setNewSession({ ...newSession, date: e.target.value })}
                                    className="md3-textfield-outlined"
                                />
                            </div>
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2">Titel</label>
                                <input
                                    type="text"
                                    value={newSession.title}
                                    onChange={(e) => setNewSession({ ...newSession, title: e.target.value })}
                                    className="md3-textfield-outlined"
                                    placeholder="z.B. Gruppenstunde"
                                />
                            </div>
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2">Typ</label>
                                <select
                                    value={newSession.type}
                                    onChange={(e) => setNewSession({ ...newSession, type: e.target.value })}
                                    className="md3-textfield-outlined"
                                >
                                    <option value="gruppenstunde">Gruppenstunde</option>
                                    <option value="event">Event</option>
                                    <option value="other">Sonstiges</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button onClick={createNewSession} className="md3-btn-filled flex-1">
                                Erstellen
                            </button>
                            <button onClick={() => setShowNewSessionModal(false)} className="md3-btn-text flex-1">
                                Abbrechen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Attendance;
